import classNames from "classnames";
import * as React from "react";

import { DeletableBox } from "../DeletableBox";

import { JsonMetadataEditor } from "./JsonMetadataEditor";
import styles from "./MetadataView.module.css";
import { Metadata } from "./types";
import { Value } from "./Value";

interface Props {
  metadata: Metadata;
  onChange?: (
    metadata: Metadata,
    diff?: {
      keysUpdated?: string[];
      keysDeleted?: string[];
    },
  ) => void;
  onDeleteMetadata?: (key: string) => void;
  className?: classNames.Argument;
  disabled?: boolean;
  jsonMetadataDisabled?: boolean;
}

export const MetadataView: React.FC<Props> = ({
  metadata,
  onChange,
  onDeleteMetadata,
  className,
  disabled = false,
  jsonMetadataDisabled = false,
}) => {
  function removeKeyFromMetadata(key: string) {
    const copy = { ...metadata };
    delete copy[key];

    if (onChange) {
      onChange(copy, { keysDeleted: [key] });
    }

    if (onDeleteMetadata) {
      onDeleteMetadata(key);
    }
  }

  const hasEntries = Object.keys(metadata).length > 0;

  return (
    <dl
      className={classNames(
        styles.root,
        hasEntries && styles.populated,
        className,
      )}
    >
      <div className={styles.grid}>
        {Object.keys(metadata)
          .sort((a, b) => {
            return a.localeCompare(b);
          })
          .map((mKey) => {
            const onDelete = () => {
              if (!disabled) {
                removeKeyFromMetadata(mKey);
              }
            };

            const value = metadata[mKey];

            let content = (
              <>
                <dt className={styles.key}>{mKey}</dt>
                <dd className={styles.value}>
                  <Value value={value} />
                </dd>
              </>
            );
            // Pardon the double-negatives, but it makes
            // for better parameter names on the outside.
            const enableEditing = !jsonMetadataDisabled && !disabled;
            if (enableEditing) {
              // Allow editing a non-disabled box.
              content = (
                <JsonMetadataEditor
                  value={value}
                  onObjectValueChanged={(newValue) => {
                    if (onChange) {
                      onChange(
                        { ...metadata, [mKey]: newValue },
                        { keysUpdated: [mKey] },
                      );
                    }
                  }}
                >
                  {content}
                </JsonMetadataEditor>
              );
            }
            return (
              <div key={mKey} className={styles.item}>
                <DeletableBox onDelete={onDelete} disabled={disabled}>
                  {content}
                </DeletableBox>
              </div>
            );
          })}
      </div>
    </dl>
  );
};
